import config from 'config'
import store from 'store'
import { selectAccountPreferenceChatEnabled } from 'selectors/app/selectAccountPreferences'
// Matrix includes
import PlatformPeg from 'matrix-react-sdk/lib/PlatformPeg'
import SdkConfig from 'matrix-react-sdk/lib/SdkConfig'
import { setTileHandler } from 'matrix-react-sdk/lib/components/views/rooms/EventTile'
import BasePlatform from 'matrix-react-sdk/lib/BasePlatform'
import SettingsStore, {
  SettingLevel,
} from 'matrix-react-sdk/lib/settings/SettingsStore'
import sdk from 'matrix-react-sdk'
import ContentMessages from 'matrix-react-sdk/lib/ContentMessages'

import counterpart from 'counterpart'
import { convertToShadow } from 'ducks/chat/utils/events'
import { MATRIX_UI_READY } from 'ducks/chat/actionTypes/client'

import { getHandlerTile } from '../utils/tiles'
import enTranslations from '../resources/i18n/en_US.json'
import { components } from '../components/component-index'

const { chatServerUrl: MATRIX_CHAT_SERVER_URL } = config

class MatrixUiEnvironment {
  constructor(storeInstance) {
    this.store = storeInstance
    this.loadingPromise = null
  }

  isChatEnabled() {
    const { getState } = this.store
    return selectAccountPreferenceChatEnabled(getState())
  }

  async loadMatrixUi() {
    if (!this.isChatEnabled()) return null
    if (this.loadingPromise) return this.loadingPromise
    const { dispatch } = this.store
    setTileHandler(getHandlerTile)
    this.loadingPromise = new Promise(async resolve => {
      await this.setupMatrixUi()
      Object.freeze(this)
      resolve(this)
      dispatch({ type: MATRIX_UI_READY })
    })
    return this.loadingPromise
  }

  async setupMatrixUi() {
    // Setup platform wrapper
    PlatformPeg.set(new BasePlatform())

    // Load and setup configuration
    // XXX: We call this twice, once here and once in MatrixChat as a prop. We call it here to ensure
    // granular settings are loaded correctly and to avoid duplicating the override logic for the theme.
    SdkConfig.put({
      default_server_config: {
        'm.homeserver': {
          base_url: config.chatServerUrl,
          server_name: 'Groove',
        },
        'm.identity_server': {
          base_url: config.chatIdentityServerUrl,
        },
      },
      brand: 'Groove',
      defaultCountryCode: 'US',
      default_theme: 'light',
    })

    // Load language after loading config.json so that settingsDefaults.language can be applied
    this.loadLanguage()

    // Load skin for matrix ui
    this.loadSkin()

    this.config = {
      // TODO: Need to remove references to the matrix integrations ui
      integrations_ui_url: 'https://scalar.vector.im/',
      integrations_rest_url: 'https://scalar.vector.im/api',
      bug_report_endpoint_url: null,
      validated_server_config: {
        hsUrl: MATRIX_CHAT_SERVER_URL,
        hsName: 'GROOVE',
        hsNameIsDifferent: true,
        isDefault: true,
        warning: null,
      },
    }
  }

  loadLanguage() {
    const langToUse = 'en'
    counterpart.registerTranslations(langToUse, enTranslations)
    counterpart.setLocale(langToUse)
    SettingsStore.setValue('language', null, SettingLevel.DEVICE, langToUse)
  }

  loadSkin() {
    sdk.resetSkin()
    sdk.loadSkin({ components })
  }

  registerOnBeforeFileSendMessageHandler({ accountChatRoomId }) {
    ContentMessages.sharedInstance().setOnBeforeSendMessageHandler(
      this.createOBeforeFileSendMessageHandler(accountChatRoomId)
    )
  }

  createOBeforeFileSendMessageHandler(accountChatRoomId) {
    return async data => {
      const { options: { isNote } = {} } = data
      // eslint-disable-next-line no-param-reassign
      data.accountChatRoomId = accountChatRoomId
      let { content } = data
      if (isNote) {
        content = await convertToShadow(data)
      }
      return content
    }
  }

  async get() {
    await this.loadMatrixUi()
    return this
  }
}

export default new MatrixUiEnvironment(store)
